import { useContext, useEffect, createRef } from 'react';
import { slide as Menu } from 'react-burger-menu';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import FocusLock, { MoveFocusInside } from 'react-focus-lock';

import { Context } from '../../Providers/Blade';
import Portal from '../../Core/Portal';
import LanguageToggle from '../../Core/LanguageToggle';

import {
  Overlay,
  Header,
  CloseBtn,
  Wrapper,
  Logo,
  RightSideHeader,
} from './styles';

const Blade = ({
  children = null,
  isOpen = false,
  title = 'Menu',
  logo = null,
  component,
}) => {
  const { resetState, closeAllMenusOnEsc } = useContext(Context);

  const intl = useIntl();

  // Create reference to blade
  const modalRef = createRef();

  useEffect(() => resetState(), []);

  useEffect(() => {
    if (isOpen === true) {
      // Create array of focusable elements within ref once the blade is mounted
      const focusableModalElements = modalRef.current.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select',
      );

      // Set the focusable element to the first element within the referenced array
      const firstElement = focusableModalElements[0];

      // If the blade is open, autofocus on first element
      // Timeout added to account for animation of blade as content is set to `visibility: hidden` via css to prevent screen readers from reading the hidden content.

      setTimeout(() => {
        firstElement.focus();
      }, 500);
    }
  }, [isOpen]);

  /**
   * Replace whitespace and special characters within string with nothing, split then join string via dashes.
   * @param {*} string
   */
  const safeId = string =>
    string
      .replace(/[^\w\s]/g, '')
      .split(' ')
      .join('-');

  const showLanguageToggle = component === 'menu-blade';

  return (
    <Portal>
      {/* Put wrapper in FocusLock and focus will be moved inside on mount, then return focus into initial position on unmount */}
      <FocusLock disabled={!isOpen} returnFocus>
        <Wrapper
          id={`outer-container-${safeId(title)}`}
          isOpen={isOpen}
          className={component}
          ref={modalRef}
        >
          <Overlay isOpen={isOpen} onClick={() => resetState()} />
          <Menu
            right
            noOverlay
            as="div"
            htmlClassName="blade-is-open"
            className={isOpen ? `is-active ${component}` : `${component}`}
            customBurgerIcon={false}
            customCrossIcon={false}
            isOpen={isOpen}
            customOnKeyDown={closeAllMenusOnEsc}
            outerContainerId={`outer-container-${safeId(title)}`}
            id={`inner-container-${safeId(title)}`}
          >
            {/* Move focus inside of FocusLock element */}
            <MoveFocusInside>
              <Header>
                {logo ? (
                  <Logo
                    src={logo.asset.localImage.publicURL}
                    alt={logo.altText || ''}
                  />
                ) : (
                  <p>{title}</p>
                )}
                <RightSideHeader>
                  <LanguageToggle
                    showShortButton
                    showAlways={showLanguageToggle}
                  />
                  <CloseBtn
                    aria-label={`${intl.formatMessage({ id: 'close' })}`}
                    onClick={() => resetState()}
                  >
                    <span>{intl.formatMessage({ id: 'close' })}</span>
                  </CloseBtn>
                </RightSideHeader>
              </Header>
            </MoveFocusInside>
            {children}
          </Menu>
        </Wrapper>
      </FocusLock>
    </Portal>
  );
};

Blade.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  // ctas: PropTypes.arrayOf(PropTypes.shape()),
  component: PropTypes.string.isRequired,
  logo: PropTypes.shape(),
};

export default Blade;
